import * as ReactDOM from 'react-dom';
import { getJson, postJson } from '../client/api';
import { URLX } from '../url';
import { type AsyncObservable, createAsyncObservable, createSyncObservable, type SyncObservable } from '../events';
import { createCache } from '../client/memory-cache';
import { createOpenPromise, type OpenPromise } from '@avensia/observable';
import { getRequestContext } from '../server/request-context';
import { resolveComponentAndChildComponents } from '../resolve-component';
export const batchWaitMS = 32;

export type DynamicDataObservable<T> = AsyncObservable<T> & {
  refresh: () => Promise<T>;
  brodcastRefreshToOtherTabsAndWindows: () => void;
  loading: SyncObservable<boolean>;
  remove: () => void;
};

// NOTE! Functions in this file typically take an optional `type: string` parameter.
// These are not really optional, but we use a TypeScript transformer to transform this call:
// const cart = resolveData<Api.CartApiModel>();
//
// Into this:
// const cart = resolveData<Api.CartApiModel>(false, undefined, 'The.Full.DotNet.Type.Name,The.Assembly');
//
// If you introduce another function that needs this, all you have to do is export it, and add it to the
// list of functions in `dotnet-type-transformer.ts`.

type CacheItem = { $cacheKey: string } & Excite.ContentApi.IDynamicData;
export type DynamicDataContext = Record<string, any> | undefined;

const cache = createCache<CacheItem>(item => item.$cacheKey, 1000);

let timer: ReturnType<typeof setTimeout> | undefined = undefined;
let toResolve: { [cacheKey: string]: Excite.ContentApi.DynamicDataIdentifier<any> } = {};
const requestsInFlight: { [cacheKey: string]: OpenPromise<void> } = {};
const observables: { [cacheKey: string]: DynamicDataObservable<unknown> } = {};

if (__DEV__ && __BROWSER__) {
  (window as any).dynamicDataObservablesForDev = observables;
}

export async function clearDynamicDataCache() {
  cache.clear();

  for (const cacheKey in observables) {
    observables[cacheKey].emit(undefined);
  }
}

export async function refreshDynamicDataCache() {
  for (const cacheKey in observables) {
    observables[cacheKey].refresh();
  }
}

function createDescriptor(
  context?: DynamicDataContext,
  type?: string,
): Excite.ContentApi.DynamicDataIdentifier<unknown> {
  return { type: type!, context };
}

export function getDynamicData<T extends Excite.ContentApi.IDynamicData>(
  context?: DynamicDataContext,
  type?: string,
): DynamicDataObservable<T> {
  if (!__BROWSER__) {
    const contextKey = 'dynamicDataObservables';
    const requestContext = getRequestContext();
    if (!(contextKey in requestContext)) {
      requestContext.additional[contextKey] = {};
    }
    const existingObservables = requestContext.additional[contextKey];

    const serverDynamicData = requestContext.initialData!.clientData.dynamicData;
    const cacheKey = getCacheKey(context, type!);
    if (cacheKey in existingObservables) {
      return existingObservables[cacheKey] as DynamicDataObservable<T>;
    }

    let observable: DynamicDataObservable<T> | undefined = undefined;
    for (const data of serverDynamicData) {
      if (data.type === type) {
        if (JSON.stringify(context) === JSON.stringify(data.context)) {
          observable = createObservable<T>(data.data as T, context, type!);
          break;
        }
      }
    }

    if (!observable) {
      observable = createObservable<T>(() => new Promise(() => undefined), context, type!);
    }

    existingObservables[cacheKey] = observable;

    return observable;
  }

  const cacheKey = getCacheKey(context, type!);

  if (!(cacheKey in observables)) {
    const cacheItem = cache.get(cacheKey) as T | undefined;
    const observable = createObservable<T>(
      cacheItem ? cacheItem : () => resolveData<T>(context, type!),
      context,
      type!,
    );
    observables[cacheKey] = observable;
  }
  return observables[cacheKey] as DynamicDataObservable<T>;
}

type Mutable<T> = {
  -readonly [P in keyof T]: T[P];
};

function createObservable<T extends Excite.ContentApi.IDynamicData>(
  dataOrResolver: T | (() => Promise<T>),
  context: DynamicDataContext,
  type: string,
): DynamicDataObservable<T> {
  // If we already have the value then dataOrResolver won't be a function
  // and we most likely don't want to notify on the first time
  // anyones requests the value. If it's a function however it means
  // that we don't have a value and will request the value on first access
  // and in that case it's likely that subscribers want to be notified.
  const notifyFirstListener = typeof dataOrResolver === 'function';
  const observable = createAsyncObservable<T>(dataOrResolver, notifyFirstListener)[0];
  const currentMethod = observable.current;
  let currentlyResolving: Promise<T> | undefined = undefined;
  const [loading] = createSyncObservable(false);
  const mutableLoading = loading as Mutable<SyncObservable<boolean>>;

  const loadingEmit = loading.emit;
  mutableLoading.emit = value => {
    // We do this because we want to avoid manually emiting `loading`
    // to cause extra emits from the built-in code. You might want to
    // emit `loading` earlier than you actually resolve the data for
    // different reasons and in that case we need to make sure we don't
    // emit the same loading state multiple times.
    if (value === loading.currentSync()) {
      return Promise.resolve([]);
    }
    return loadingEmit.call(mutableLoading, value);
  };

  return Object.assign(observable, {
    refresh: () => {
      currentlyResolving = resolveData<T>(context, type!);
      currentlyResolving.then(
        () => {
          currentlyResolving = undefined;
        },
        () => {
          currentlyResolving = undefined;
        },
      );
      return currentlyResolving;
    },
    current: () => {
      if (currentlyResolving) {
        return currentlyResolving;
      }
      return currentMethod.apply(observable);
    },
    brodcastRefreshToOtherTabsAndWindows: () => {
      broadcastRefresh(context, type);
    },
    loading,
    remove: () => {
      const cacheKey = getCacheKey(context, type!);
      if (context) {
        cache.remove(cacheKey);
        delete observables[cacheKey];
      } else {
        const cacheKeyPrefix = cacheKey.replace('{}', '');
        cache.removeAll(cacheKeyPrefix);
        for (const key in observables) {
          if (key.startsWith(cacheKeyPrefix)) {
            delete observables[key];
          }
        }
      }
    },
  });
}

export async function initFromInitialData(initialData: Excite.ContentApi.ClientData) {
  batchEmit(() => {
    for (const dynamicData of initialData.dynamicData) {
      if (!isDynamicDataLoadFailure(dynamicData.data)) {
        addToDynamicDataCache(dynamicData.data, dynamicData.context, dynamicData.type);
      }
    }
  });

  if (initialData.dynamicDataPushInfo) {
    const networkPromise = getJson(new URLX(initialData.dynamicDataPushInfo.url), {}, false);
    const openPromise = createOpenPromise<void>();
    networkPromise.then(openPromise.resolve, openPromise.reject);
    const cacheKeys: string[] = [];
    for (const typeName in initialData.dynamicDataPushInfo.types) {
      const context = (
        initialData.dynamicDataPushInfo.types[typeName] ? initialData.dynamicDataPushInfo.types[typeName] : undefined
      ) as DynamicDataContext;
      const cacheKey = getCacheKey(context, typeName);
      requestsInFlight[cacheKey] = openPromise;
      cacheKeys.push(cacheKey);
    }
    const result: any[] = await networkPromise;
    batchEmit(() => {
      for (let i = 0; i < cacheKeys.length; i++) {
        if (!isDynamicDataLoadFailure(result[i])) {
          addToDynamicDataCache(result[i], cacheKeys[i]);
        }
      }
    });
  }
}

async function resolveData<T extends Excite.ContentApi.IDynamicData>(context: DynamicDataContext, type: string) {
  const descriptor = createDescriptor(context, type);
  const cacheKey = getCacheKey(context, type);
  const observable = observables[cacheKey] as DynamicDataObservable<T> | undefined;
  if (observable) {
    await observable.loading.emit(true);
  }
  const payload = {
    ...descriptor,
  };
  if (!payload.context) {
    delete payload.context;
  }

  if (!(cacheKey in toResolve)) {
    toResolve[cacheKey] = payload;
    requestsInFlight[cacheKey] = createOpenPromise();
  }
  if (timer !== undefined) {
    clearTimeout(timer);
  }
  timer = setTimeout(async () => {
    const resolvesToSend = toResolve;
    toResolve = {};

    // The sorting here isn't just because it's fun, it's because we need to ensure a determinitic format
    // for the json in the query string. The reason for that is that we use HTTP2 push/preloading of
    // dynamic data. In order to do that we have to anticipate the exact url that the backend pushes.
    // The same ordering (and removal of some properties) is done in the backend. If you change this in some
    // way please make sure that you make the same change in the backend.
    const sortedResolves = Object.values(resolvesToSend);
    sortDynamicDataResolves(sortedResolves);
    const url = new URLX('/api/client/dynamic-data/resolve');
    url.searchParams.append('language', window.INITIAL_DATA.currentLanguage);

    // We don't include the default headers here (such as X-Requested-With since that triggers a
    // bug in Chrome where a preloaded response isn't used)
    try {
      let results: any[] = [];
      const ddString = generateDynamicDataQueryString(sortedResolves);
      if (window.DD_POST) {
        results = await postJson(url, { d: ddString });
      } else {
        url.searchParams.append('d', ddString);
        results = await getJson(url, {}, false);
      }

      const maybePromise = resolveComponentAndChildComponents(results);
      if (maybePromise) {
        await maybePromise;
      }

      batchEmit(() => {
        Object.keys(resolvesToSend).forEach(async sentCacheKey => {
          const sentResolve = resolvesToSend[sentCacheKey];
          const index = sortedResolves.indexOf(sentResolve);
          const isLoadFailure = isDynamicDataLoadFailure(results[index]);

          if (!isLoadFailure) {
            addToDynamicDataCache(results[index], sentCacheKey);
          }

          if (!!requestsInFlight[sentCacheKey]) {
            if (!isLoadFailure) {
              requestsInFlight[sentCacheKey].resolve(undefined);
            } else {
              requestsInFlight[sentCacheKey].reject(undefined);
            }
            delete requestsInFlight[sentCacheKey];
          }
          const observable = observables[sentCacheKey] as DynamicDataObservable<T> | undefined;
          if (observable) {
            await observable.loading.emit(false);
          }
        });
      });
    } catch (e) {
      Object.keys(resolvesToSend).forEach(sentCacheKey => {
        if (!!requestsInFlight[sentCacheKey]) {
          requestsInFlight[sentCacheKey].reject(e);
          delete requestsInFlight[sentCacheKey];
        }
      });
    }
  }, batchWaitMS);

  await requestsInFlight[cacheKey];
  return cache.get(cacheKey) as any as T;
}

function sortDynamicDataResolves(resolves: Excite.ContentApi.DynamicDataIdentifier<any>[]) {
  resolves.sort((a, b) => {
    if (a.type < b.type) {
      return -1;
    }
    if (a.type > b.type) {
      return 1;
    }
    return 0;
  });
}

function generateDynamicDataQueryString(sortedResolves: Excite.ContentApi.DynamicDataIdentifier<any>[]) {
  let lastType: string | undefined = undefined;
  const compressedResolvesJsons = sortedResolves.map(r => {
    let type: string | undefined = r.type;
    if (lastType !== type) {
      lastType = type;
    } else {
      type = undefined;
    }

    const props: string[] = [];
    if (type) {
      props.push('t:"' + type + '"');
    }
    if (r.context) {
      props.push('c:' + JSON.stringify(r.context));
    }
    const json = '{' + props.join(',') + '}';
    return json;
  });

  const allCompressedResolvesJson = '[' + compressedResolvesJsons.join(',') + ']';
  return allCompressedResolvesJson;
}

function getCacheKey(context: DynamicDataContext, type: string) {
  const cacheContext: { [key: string]: unknown | undefined } = {};
  if (context) {
    const keys = Object.keys(context);
    keys.sort();
    for (const key of keys) {
      cacheContext[key] = context[key];
    }
  }

  return type + JSON.stringify(cacheContext);
}

export function addToDynamicDataCache(
  data: Excite.ContentApi.IDynamicData,
  contextOrCacheKey: DynamicDataContext | string,
  type?: string,
) {
  const cacheKey = typeof contextOrCacheKey === 'string' ? contextOrCacheKey : getCacheKey(contextOrCacheKey, type!);
  let cacheItem = {
    ...data,
    ...{ $cacheKey: cacheKey },
  };

  const previousCacheItem = cache.get(cacheKey);
  if (previousCacheItem) {
    // If this returns true, it means that the current and new object was exactly the same
    // and in that case we reuse the old value to be able to short circuit rendering without
    // having to do deep comparision in components.
    //
    // If this return false any properties that were exactly the same is copied from the previous
    // to the new, to make it possible
    if (copyReferencesForEqualValues(cacheItem, previousCacheItem)) {
      cacheItem = previousCacheItem;
    }
  }

  cache.set(cacheItem);

  if (cacheKey in observables) {
    observables[cacheKey].emit(cacheItem);
  }
}

/**
 * Use this function to emit values to multiple observables
 * at once, like:
 *
 * batchEmit(() => {
 *   observable1.emit(1);
 *   observable2.emit(2);
 * });
 *
 * Note that the function you pass in cannot be an async function.
 * If you want to batch updates in an async flow you should call
 * batchEmit() multiple times, like:
 *
 * async function doSomething() {
 *   batchEmit(() => {
 *     observable1.emit(1);
 *     observable2.emit(2);
 *   });
 *
 *   const response = await someAsyncOperation();
 *
 *   batchEmit(() => {
 *     observable1.emit(response.value1);
 *     observable2.emit(response.value2);
 *   });
 * }
 */
export function batchEmit(func: () => void) {
  // TODO: Batch inside observables as well, so that
  // we group emits to happen after `func` has executed.
  // The observables .current() value should still be
  // updated directly after .emit() is called, but
  // subscribers for all affected observables should be called
  // at the same time after `func` has executed.
  ReactDOM.unstable_batchedUpdates(func);
}

function copyReferencesForEqualValues(newValue: unknown, previousValue: unknown) {
  let allPropertiesHaveSameValue = true;
  if (newValue && typeof newValue === 'object' && previousValue && typeof previousValue === 'object') {
    const newRecord = newValue as Record<string, unknown>;
    const previousRecord = previousValue as Record<string, unknown>;

    const newKeys = Object.keys(newRecord);
    const previousKeys = Object.keys(previousRecord);

    if (newKeys.length !== previousKeys.length) {
      allPropertiesHaveSameValue = false;
    }

    for (const key of newKeys) {
      if (
        newRecord[key] &&
        typeof newRecord[key] === 'object' &&
        key in previousRecord &&
        previousRecord[key] &&
        typeof previousRecord[key] === 'object'
      ) {
        if (!copyReferencesForEqualValues(newRecord[key], previousRecord[key])) {
          allPropertiesHaveSameValue = false;
        } else {
          newRecord[key] = previousRecord[key];
        }
      } else if (newRecord[key] !== previousRecord[key]) {
        allPropertiesHaveSameValue = false;
      }
    }
  } else if (newValue !== previousValue) {
    allPropertiesHaveSameValue = false;
  }
  return allPropertiesHaveSameValue;
}

function isDynamicDataLoadFailure(o: any): o is Excite.ContentApi.DynamicDataLoadFailure {
  return !!o && !!(o as Excite.ContentApi.DynamicDataLoadFailure).isDynamicDataLoadFailure;
}

const thisWindowId = Math.random().toString();

type BroadcastRefreshMessage = {
  context: DynamicDataContext;
  type: string;
  windowId: string;
};

const broadcastKey = 'ExciteDynamicDataBroadCast';
if (__BROWSER__) {
  window.addEventListener('storage', onStorageValueSet);

  let cacheKeysToRefresh: string[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined = undefined;

  function onStorageValueSet(ev: any) {
    if (ev.key === broadcastKey) {
      const message: BroadcastRefreshMessage = JSON.parse(ev.newValue);
      if (message.type && message.windowId && message.windowId !== thisWindowId) {
        const cacheKey = getCacheKey(message.context, message.type);
        if (cacheKey in observables) {
          cacheKeysToRefresh.push(cacheKey);
          if (timer !== undefined) {
            clearTimeout(timer);
          }
          timer = setTimeout(refresh, 500);
        }
      }
    }
  }

  function refresh() {
    for (const cacheKey of cacheKeysToRefresh) {
      observables[cacheKey].refresh();
    }
    cacheKeysToRefresh = [];
  }
}

function broadcastRefresh(context: DynamicDataContext, type: string) {
  if (__BROWSER__) {
    const message: BroadcastRefreshMessage = {
      context,
      type,
      windowId: thisWindowId,
    };
    localStorage.setItem(broadcastKey, JSON.stringify(message));
  }
}
